'use client';

/* ============================================================================
 * ProductPageV2 - shared template for the 8 platform product pages.
 * ============================================================================
 * Linear-light treatment, mirrors the homepage rhythm. Driven entirely by a
 * `content` config object so each product page is a thin wrapper that imports
 * this template and supplies its own copy + live UI scene.
 *
 * Section order (up to 11 anchors, optional sections collapse):
 *   1   Hero            - oversized headline + live UI scene
 *   2   Capabilities    - 4-6 capability bullets in a grid
 *   3   Deep dive 1     - split layout, scene on the right
 *   4   Deep dive 2     - split layout, scene on the left
 *   5   Deep dive 3     - split layout, scene on the right
 *   6   Use cases       - 3 vertical cards, real operational scenarios
 *   7   Integrations    - 2-column list of supported integrations
 *   8   Compare         - feature matrix vs alternatives (opt-in)
 *   9   Plans           - 3-tier pricing + add-ons (opt-in via content.plans)
 *  10   Testimonial     - large pull-quote + pricing teaser
 *  11   Closing         - tailored quote / talk-to-sales CTA
 *
 * Each `deepDives` entry can swap between a `scene` (live React UI) or a
 * `bullets` list (textual). Sections collapse gracefully if no entry is
 * supplied for that index, so a product with only 2 deep dives still reads
 * well.
 * ========================================================================= */

import React, {useEffect, useId, useMemo, useState} from 'react';
import {useTranslations} from 'next-intl';
import {Link} from '@/i18n/navigation';
import {Reveal, RevealStagger, RevealLine} from '@/components/v2/Reveal';
import PlatformPills from '@/components/v2/PlatformPills';
import {normalize as normalizeForSearch} from '@/content/v2/channel-integrations';

/* ---------------------------------------------------------------------------
 * Layout primitives.
 * ------------------------------------------------------------------------ */

function Container({children, className = '', narrow = false}) {
  return (
    <div className={`${narrow ? 'container-narrow' : 'container-page'} ${className}`}>
      {children}
    </div>
  );
}

function SectionAnchor({id, label, total}) {
  return (
    <Reveal className="mb-10 flex items-center gap-3 text-foreground-subtle">
      <span className="font-mono text-[11px] tracking-[0.16em] uppercase">
        {String(id).padStart(2, '0')} / {String(total).padStart(2, '0')}
      </span>
      <RevealLine className="block h-px flex-1 bg-border" delay={0.1} />
      <span className="font-mono text-[11px] tracking-[0.16em] uppercase">
        {label}
      </span>
    </Reveal>
  );
}

function SectionDivider() {
  return (
    <Container>
      <div className="divider-section" />
    </Container>
  );
}

function CheckIcon({className = ''}) {
  return (
    <svg className={className} viewBox="0 0 14 14" fill="none" aria-hidden>
      <path
        d="M11.5 4L5.5 10L2.5 7"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Render a navigation link, falling back to a native anchor for in-page
 * hash hrefs so we don't trip the locale-aware next-intl Link.
 */
function MaybeLink({href, className, children}) {
  if (href && href.startsWith('#')) {
    return (
      <a href={href} className={className}>
        {children}
      </a>
    );
  }
  return (
    <Link href={href} className={className}>
      {children}
    </Link>
  );
}

/* ---------------------------------------------------------------------------
 * 1 - Hero
 * ------------------------------------------------------------------------ */

function SectionHero({content, total}) {
  const {hero, meta} = content;
  return (
    <section className="bg-hero-mesh relative overflow-hidden pt-32 pb-24 md:pt-40 md:pb-32">
      <Container>
        <SectionAnchor id={1} label={meta.eyebrow} total={total} />

        <div className="grid grid-cols-1 gap-12 md:grid-cols-12 md:gap-10">
          <div className="md:col-span-6">
            <Reveal eager>
              <p className="fig-label">
                {meta.eyebrow} &middot; {meta.name}
              </p>
            </Reveal>
            <Reveal delay={0.06} eager>
              <h1 className="headline-hero mt-5 max-w-[28ch] text-foreground">
                {hero.headline}
              </h1>
            </Reveal>
            <Reveal delay={0.12} eager>
              <p className="lead mt-7 max-w-[52ch]">{hero.lead}</p>
            </Reveal>
            {hero.platforms !== false && (
              <Reveal delay={0.16} eager>
                <PlatformPills
                  className="mt-7"
                  items={
                    Array.isArray(hero.platforms) ? hero.platforms : undefined
                  }
                />
              </Reveal>
            )}
            <Reveal delay={0.2} eager>
              <div className="mt-10 flex flex-wrap items-center gap-5">
                <Link href={hero.primaryCta.href} className="cta-pill-brand">
                  {hero.primaryCta.label} <span aria-hidden>&rarr;</span>
                </Link>
                <Link href={hero.secondaryCta.href} className="cta-text-brand">
                  {hero.secondaryCta.label} <span aria-hidden>&rarr;</span>
                </Link>
              </div>
            </Reveal>

            {hero.stats && hero.stats.length > 0 && (
              <Reveal delay={0.24} eager>
                <dl className="mt-12 grid grid-cols-3 gap-6 border-t border-border pt-6">
                  {hero.stats.map((s) => (
                    <div key={s.label}>
                      <dt className="font-mono text-[11px] tracking-[0.16em] uppercase text-foreground-subtle">
                        {s.label}
                      </dt>
                      <dd className="mt-2 text-xl font-medium text-foreground tracking-tight">
                        {s.value}
                      </dd>
                    </div>
                  ))}
                </dl>
              </Reveal>
            )}
          </div>

          <div className="md:col-span-6">
            <Reveal delay={0.18} eager>
              <div className="relative">
                <div className="glow-brand-soft" />
                <div className="relative">{hero.scene}</div>
              </div>
            </Reveal>
          </div>
        </div>
      </Container>
    </section>
  );
}

/* ---------------------------------------------------------------------------
 * 2 - Capabilities
 * ------------------------------------------------------------------------ */

function SectionCapabilities({content, total}) {
  const {capabilities} = content;
  const t = useTranslations('Templates.ProductPage.anchors');
  return (
    <section className="section-rhythm">
      <Container>
        <SectionAnchor id={2} label={t('capabilities')} total={total} />

        <div className="mb-12 grid grid-cols-1 gap-10 md:grid-cols-12">
          <div className="md:col-span-7">
            <Reveal>
              <h2 className="headline-section text-foreground max-w-[20ch]">
                {capabilities.headline}
              </h2>
            </Reveal>
          </div>
          <div className="md:col-span-5">
            <Reveal delay={0.06}>
              <p className="lead">{capabilities.lead}</p>
            </Reveal>
          </div>
        </div>

        <RevealStagger
          as="ul"
          mode="clone"
          className="grid grid-cols-1 divide-y divide-border border-y border-border md:grid-cols-2 md:divide-y-0 md:divide-x lg:grid-cols-3"
          gap={0.05}
        >
          {capabilities.items.map((item, i) => (
            <li
              key={item.title}
              className={
                'p-7 md:p-8' +
                (i >= 3 ? ' lg:border-t lg:border-border' : '')
              }
            >
              <p className="font-mono text-[11px] tracking-[0.16em] uppercase text-foreground-subtle">
                {String(i + 1).padStart(2, '0')}
              </p>
              <h3 className="mt-3 text-lg font-medium text-foreground tracking-tight">
                {item.title}
              </h3>
              <p className="mt-3 text-[14px] leading-relaxed text-foreground-muted">
                {item.body}
              </p>
            </li>
          ))}
        </RevealStagger>
      </Container>
    </section>
  );
}

/* ---------------------------------------------------------------------------
 * 3-5 - Deep dive (split layout, alternating)
 * ------------------------------------------------------------------------ */

function SectionDeepDive({entry, index, total, anchorId, label}) {
  const tDD = useTranslations('Templates.ProductPage.deepDive');
  if (!entry) return null;

  const reverse = index % 2 === 1; // alternate sides
  const hasScene = Boolean(entry.scene);
  const hasBullets = Array.isArray(entry.bullets) && entry.bullets.length > 0;

  const visualSide = (
    <Reveal delay={0.18}>
      {hasScene ? (
        <div className="relative">
          <div className="glow-brand-soft" />
          <div className="relative">{entry.scene}</div>
        </div>
      ) : hasBullets ? (
        <div className="surface-elevated p-7 md:p-8">
          <p className="fig-label">{entry.bulletsLabel || tDD('bulletsLabelHowItWorks')}</p>
          <ul className="mt-5 space-y-4">
            {entry.bullets.map((b) => (
              <li key={b} className="flex items-start gap-3 text-[14px] leading-relaxed text-foreground">
                <CheckIcon className="mt-1 size-3.5 flex-shrink-0 text-primary" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </Reveal>
  );

  // When BOTH a scene and bullets are provided, the scene goes in the visual
  // slot (right or left, alternating) and the bullets render full-width below
  // both columns as a dense capability checklist. This pattern is used for
  // dense product surfaces (Automations, Reports) where the scene shows one
  // slice and the bullets list the breadth.
  const showInlineBullets = hasScene && hasBullets;

  const copySide = (
    <div>
      <Reveal>
        <p className="fig-label">{entry.eyebrow || `Deep dive 0${index + 1}`}</p>
      </Reveal>
      <Reveal delay={0.06}>
        <h2 className="headline-section mt-4 max-w-[20ch] text-foreground">
          {entry.headline}
        </h2>
      </Reveal>
      <Reveal delay={0.12}>
        <p className="lead mt-6 max-w-[44ch]">{entry.body}</p>
      </Reveal>
      {entry.cta && (
        <Reveal delay={0.18}>
          <Link href={entry.cta.href} className="cta-text-brand mt-8">
            {entry.cta.label} <span aria-hidden>&rarr;</span>
          </Link>
        </Reveal>
      )}
    </div>
  );

  return (
    <section className={`section-rhythm ${index % 2 === 0 ? '' : 'bg-background-subtle'}`}>
      <Container>
        <SectionAnchor id={anchorId} label={label} total={total} />
        <div className="grid grid-cols-1 items-center gap-12 md:grid-cols-12 md:gap-12">
          <div className={`md:col-span-5 ${reverse ? 'md:order-2' : ''}`}>
            {copySide}
          </div>
          <div className={`md:col-span-7 ${reverse ? 'md:order-1' : ''}`}>
            {visualSide}
          </div>
        </div>

        {showInlineBullets && (
          <Reveal delay={0.24} className="mt-12">
            <div className="border-t border-border-subtle pt-8">
              <p className="fig-label">{entry.bulletsLabel || tDD('bulletsLabelInProduct')}</p>
              <ul className="mt-5 grid grid-cols-1 gap-x-10 gap-y-4 md:grid-cols-2">
                {entry.bullets.map((b) => (
                  <li
                    key={b}
                    className="flex items-start gap-3 text-[14px] leading-relaxed text-foreground"
                  >
                    <CheckIcon className="mt-1 size-3.5 flex-shrink-0 text-primary" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        )}
      </Container>
    </section>
  );
}

/* ---------------------------------------------------------------------------
 * 6 - Use cases
 * ------------------------------------------------------------------------ */

function SectionUseCases({content, anchorId, total}) {
  const {useCases} = content;
  const t = useTranslations('Templates.ProductPage.anchors');
  if (!useCases) return null;

  return (
    <section className="section-rhythm">
      <Container>
        <SectionAnchor id={anchorId} label={t('useCases')} total={total} />

        <div className="mb-12 grid grid-cols-1 gap-10 md:grid-cols-12">
          <div className="md:col-span-7">
            <Reveal>
              <h2 className="headline-section text-foreground max-w-[20ch]">
                {useCases.headline}
              </h2>
            </Reveal>
          </div>
          <div className="md:col-span-5">
            <Reveal delay={0.06}>
              <p className="lead">{useCases.lead}</p>
            </Reveal>
          </div>
        </div>

        <RevealStagger
          mode="clone"
          className="grid grid-cols-1 gap-6 md:grid-cols-3"
          gap={0.08}
        >
          {useCases.items.map((u) => (
            <article key={u.title} className="surface-elevated flex h-full flex-col p-7">
              <p className="fig-label">{u.who}</p>
              <h3 className="mt-3 text-lg font-medium text-foreground tracking-tight">
                {u.title}
              </h3>
              <p className="mt-3 flex-1 text-[14px] leading-relaxed text-foreground-muted">
                {u.body}
              </p>
            </article>
          ))}
        </RevealStagger>
      </Container>
    </section>
  );
}

/* ---------------------------------------------------------------------------
 * 7 - Integrations
 * ------------------------------------------------------------------------ */

function SectionIntegrations({content, anchorId, total}) {
  const {integrations} = content;
  if (!integrations) return null;
  if (integrations.searchable) {
    return (
      <SectionIntegrationsSearchable
        integrations={integrations}
        anchorId={anchorId}
        total={total}
      />
    );
  }
  return (
    <SectionIntegrationsSimple
      integrations={integrations}
      anchorId={anchorId}
      total={total}
    />
  );
}

function SectionIntegrationsSimple({integrations, anchorId, total}) {
  const t = useTranslations('Templates.ProductPage.anchors');
  return (
    <section className="section-rhythm bg-background-subtle">
      <Container>
        <SectionAnchor id={anchorId} label={t('integrations')} total={total} />

        <div className="grid grid-cols-1 gap-12 md:grid-cols-12 md:gap-12">
          <div className="md:col-span-5">
            <Reveal>
              <h2 className="headline-section text-foreground max-w-[18ch]">
                {integrations.headline}
              </h2>
            </Reveal>
            <Reveal delay={0.06}>
              <p className="lead mt-6 max-w-[40ch]">{integrations.lead}</p>
            </Reveal>
          </div>
          <div className="md:col-span-7">
            <RevealStagger
              as="ul"
              mode="clone"
              className="grid grid-cols-2 divide-y divide-x divide-border border border-border md:grid-cols-3"
              gap={0.04}
            >
              {integrations.items.map((item, i) => (
                <li
                  key={item.name + i}
                  className="flex flex-col gap-1.5 p-5"
                >
                  <span className="text-[14px] font-medium text-foreground">
                    {item.name}
                  </span>
                  <span className="font-mono text-[10.5px] tracking-[0.12em] uppercase text-foreground-subtle">
                    {item.category}
                  </span>
                </li>
              ))}
            </RevealStagger>
          </div>
        </div>
      </Container>
    </section>
  );
}

/* ---------------------------------------------------------------------------
 * Searchable directory variant.
 *
 * When `integrations.searchable` is true, render a search input + category
 * filter chips + counter + "no results" empty state. Used by the channel
 * manager page where the list is hundreds of partners long.
 *
 * Expected shape:
 *   integrations.items[i] = {name, category, region?}
 *   integrations.categories  = [{id, label}, ...]   // for filter chips
 *   integrations.totalLabel  = '200+ channels'      // optional headline tag
 * ------------------------------------------------------------------------ */
const DEFAULT_VISIBLE = 30;
const LOAD_MORE_STEP = 30;

function SectionIntegrationsSearchable({integrations, anchorId, total}) {
  const inputId = useId();
  const [query, setQuery] = useState('');
  const [activeCat, setActiveCat] = useState('all');
  const [cap, setCap] = useState(DEFAULT_VISIBLE);
  const tA = useTranslations('Templates.ProductPage.anchors');
  const tS = useTranslations('Templates.ProductPage.search');

  const items = integrations.items || [];
  const categories = integrations.categories || [];

  const visible = useMemo(() => {
    const q = normalizeForSearch(query.trim());
    return items.filter((it) => {
      if (activeCat !== 'all' && it.category !== activeCat) return false;
      if (!q) return true;
      return (
        normalizeForSearch(it.name).includes(q) ||
        normalizeForSearch(it.category).includes(q) ||
        normalizeForSearch(it.region).includes(q)
      );
    });
  }, [items, query, activeCat]);

  // Pagination only kicks in when no filter / search is active. Anything
  // narrower than the full list shows everything that matches.
  const isFiltered = activeCat !== 'all' || query.trim().length > 0;
  const effectiveCap = isFiltered ? visible.length : cap;
  const visibleSlice = visible.slice(0, effectiveCap);
  const remaining = Math.max(visible.length - effectiveCap, 0);

  useEffect(() => {
    setCap(DEFAULT_VISIBLE);
  }, [activeCat, query]);

  const catLabel = (catId) => {
    if (catId === 'all') return tS('allFilter');
    return categories.find((c) => c.id === catId)?.label || catId;
  };

  const counts = useMemo(() => {
    const map = {all: items.length};
    for (const it of items) {
      map[it.category] = (map[it.category] || 0) + 1;
    }
    return map;
  }, [items]);

  const handleLoadMore = () => {
    setCap((c) => Math.min(c + LOAD_MORE_STEP, visible.length));
  };
  const handleShowAll = () => setCap(visible.length);

  return (
    <section className="section-rhythm bg-background-subtle">
      <Container>
        <SectionAnchor id={anchorId} label={tA('integrations')} total={total} />

        {/* Header: left-aligned headline, lead + totalLabel right of it on
            desktop. Sits above the directory at the full container width. */}
        <div className="grid grid-cols-1 gap-8 md:grid-cols-12 md:gap-12">
          <div className="md:col-span-7">
            <Reveal>
              <h2 className="headline-section text-foreground max-w-[20ch]">
                {integrations.headline}
              </h2>
            </Reveal>
          </div>
          <div className="md:col-span-5 md:flex md:flex-col md:justify-end">
            <Reveal delay={0.06}>
              <p className="lead max-w-[44ch]">{integrations.lead}</p>
            </Reveal>
            {integrations.totalLabel && (
              <Reveal delay={0.12}>
                <p className="mt-5 font-mono text-[11px] tracking-[0.16em] uppercase text-foreground-subtle">
                  {integrations.totalLabel}
                </p>
              </Reveal>
            )}
          </div>
        </div>

        {/* Search + chips toolbar, full container width. */}
        <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-12 md:items-center md:gap-6">
          <Reveal className="md:col-span-5">
            <div className="relative">
              <label htmlFor={inputId} className="sr-only">
                Search integrations
              </label>
              <SearchIcon className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-foreground-subtle" />
              <input
                id={inputId}
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={tS('placeholder', {n: items.length})}
                autoComplete="off"
                className="block w-full rounded-md border border-border bg-background px-12 py-3 text-[14px] text-foreground placeholder:text-foreground-subtle focus:border-foreground focus:outline-none focus:ring-0"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery('')}
                  aria-label={tS('clearLabel')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 rounded-sm p-1 text-foreground-subtle hover:text-foreground"
                >
                  <CloseIcon className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </Reveal>
          {categories.length > 0 && (
            <Reveal className="md:col-span-7" delay={0.04}>
              <div
                role="tablist"
                aria-label={tS('filterCategoryLabel')}
                className="-mx-1 flex flex-wrap gap-1.5 md:justify-end"
              >
                <FilterChip
                  label={`${tS('allFilter')} (${counts.all || 0})`}
                  active={activeCat === 'all'}
                  onClick={() => setActiveCat('all')}
                />
                {categories.map((c) => (
                  <FilterChip
                    key={c.id}
                    label={`${c.label} (${counts[c.id] || 0})`}
                    active={activeCat === c.id}
                    onClick={() => setActiveCat(c.id)}
                  />
                ))}
              </div>
            </Reveal>
          )}
        </div>

        <Reveal delay={0.08}>
          <p className="mt-6 font-mono text-[11px] tracking-[0.14em] uppercase text-foreground-subtle">
            {isFiltered
              ? tS('ofIntegrations', {visible: visible.length, total: items.length})
              : tS('ofIntegrations', {visible: visibleSlice.length, total: items.length})}
            {activeCat !== 'all' && ` · ${catLabel(activeCat)}`}
            {query && ` · "${query}"`}
          </p>
        </Reveal>

        {/* Directory grid, full container width. */}
        {visible.length === 0 ? (
          <div className="mt-6 rounded-md border border-dashed border-border bg-background p-10 text-center">
            <p className="font-display text-base tracking-tight text-foreground md:text-lg">
              {tS('noMatches', {query})}
            </p>
            <p className="mt-2 text-sm leading-relaxed text-foreground-subtle">
              {tS('noMatchesPrompt')}{' '}
              <Link href="/become-a-partner/" className="underline">
                {tS('noMatchesCta')}
              </Link>
              .
            </p>
          </div>
        ) : (
          <ul
            key={`${activeCat}|${query}`}
            className="mt-6 grid grid-cols-2 divide-y divide-x divide-border overflow-hidden rounded-md border border-border bg-background sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5"
          >
            {visibleSlice.map((item, i) => (
              <li
                key={item.name + i}
                className="flex flex-col gap-1.5 p-4"
              >
                <span className="text-[14px] font-medium text-foreground">
                  {item.name}
                </span>
                <span className="font-mono text-[10.5px] tracking-[0.12em] uppercase text-foreground-subtle">
                  {catLabel(item.category)}
                </span>
              </li>
            ))}
          </ul>
        )}

        {remaining > 0 && (
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <button
              type="button"
              onClick={handleLoadMore}
              className="rounded-full border border-foreground bg-background px-5 py-2.5 font-mono text-[11px] tracking-[0.16em] uppercase text-foreground transition-colors hover:bg-foreground hover:text-background"
            >
              {remaining > LOAD_MORE_STEP
                ? tS('loadMore', {n: LOAD_MORE_STEP})
                : tS('loadMore', {n: remaining})}
            </button>
            {remaining > LOAD_MORE_STEP && (
              <button
                type="button"
                onClick={handleShowAll}
                className="font-mono text-[11px] tracking-[0.16em] uppercase text-foreground-subtle hover:text-foreground"
              >
                {tS('showAll', {n: visible.length})}
              </button>
            )}
          </div>
        )}

        <p className="mt-10 text-center text-sm leading-relaxed text-foreground-subtle">
          {tS('lookingFor')}{' '}
          <Link href="/become-a-partner/" className="underline">
            {tS('tellUs')}
          </Link>{' '}
          {tS('andScope')}
        </p>
      </Container>
    </section>
  );
}

function FilterChip({label, active, onClick}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={
        'rounded-full border px-3.5 py-1.5 font-mono text-[11px] tracking-[0.12em] uppercase transition-colors ' +
        (active
          ? 'border-foreground bg-foreground text-background'
          : 'border-border bg-background text-foreground-subtle hover:border-foreground hover:text-foreground')
      }
    >
      {label}
    </button>
  );
}

function SearchIcon({className}) {
  return (
    <svg
      className={className}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <circle cx="7" cy="7" r="5" />
      <path d="M11 11l3 3" />
    </svg>
  );
}

function CloseIcon({className}) {
  return (
    <svg
      className={className}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <path d="M3 3l10 10M13 3L3 13" />
    </svg>
  );
}

/* ---------------------------------------------------------------------------
 * 8 - Comparison table (optional, opt-in via content.comparison)
 *
 * Renders a Linear-style 2-column feature comparison ("Prostay X" vs
 * "Standalone tools"). Drives off content.comparison.columns and
 * content.comparison.groups; row values can be `true`, `false` or a string.
 *
 * Sticky column header keeps the column labels visible while the table
 * scrolls vertically (md+ only - on small screens the wrapper falls back
 * to horizontal scroll so the table doesn't crush).
 * ------------------------------------------------------------------------ */

function SectionComparison({content, anchorId, total}) {
  const {comparison} = content;
  const t = useTranslations('Templates.ProductPage.anchors');
  if (!comparison || !comparison.groups || comparison.groups.length === 0) {
    return null;
  }

  const cols = comparison.columns || [];
  if (cols.length < 2) return null;

  return (
    <section className="section-rhythm bg-background-subtle">
      <Container>
        <SectionAnchor id={anchorId} label={t('compare')} total={total} />

        <div className="grid grid-cols-1 gap-10 md:grid-cols-12 md:gap-12">
          <div className="md:col-span-7">
            <Reveal>
              <h2 className="headline-section text-foreground max-w-[22ch]">
                {comparison.headline}
              </h2>
            </Reveal>
          </div>
          <div className="md:col-span-5 md:flex md:flex-col md:justify-end">
            <Reveal delay={0.06}>
              <p className="lead max-w-[44ch]">{comparison.lead}</p>
            </Reveal>
          </div>
        </div>

        <ComparisonTable comparison={comparison} />
      </Container>
    </section>
  );
}

function ComparisonTable({comparison}) {
  const cols = comparison.columns;
  const t = useTranslations('Templates.ProductPage.comparison');
  return (
    <Reveal className="mt-14">
      {/* On small screens, allow horizontal scroll if columns overflow.
          On md+, drop the overflow wrapper so position: sticky on the
          column header is anchored to the viewport, not a scroll context. */}
      <div className="overflow-x-auto md:overflow-visible">
        <div className="min-w-[640px] md:min-w-0">
          <div className="sticky top-20 z-20 border-y border-border bg-background-subtle/95 backdrop-blur-sm">
            <div className="grid grid-cols-12 items-end gap-4 py-5">
              <div className="col-span-6">
                <p className="fig-label">{t('featureLabel')}</p>
              </div>
              {cols.map((c, i) => (
                <div
                  key={c.label || i}
                  className={`col-span-3 ${i === 0 ? 'text-foreground' : 'text-foreground-muted'}`}
                >
                  <p
                    className={`text-base font-medium tracking-tight md:text-lg ${
                      i === 0 ? 'text-foreground' : 'text-foreground-muted'
                    }`}
                  >
                    {c.label}
                  </p>
                  {c.sublabel && (
                    <p className="mt-0.5 font-mono text-[11px] tracking-[0.12em] uppercase text-foreground-subtle">
                      {c.sublabel}
                    </p>
                  )}
                  {c.cta && (
                    <MaybeLink
                      href={c.cta.href}
                      className="cta-text-brand mt-2 text-[12px]"
                    >
                      {c.cta.label} <span aria-hidden>&rarr;</span>
                    </MaybeLink>
                  )}
                </div>
              ))}
            </div>
          </div>

          {comparison.groups.map((group) => (
            <ComparisonGroup
              key={group.label}
              group={group}
              colKeys={cols.map((c) => c.key)}
            />
          ))}
        </div>
      </div>
    </Reveal>
  );
}

function ComparisonGroup({group, colKeys}) {
  return (
    <section className="mt-10">
      <div className="grid grid-cols-12 items-baseline gap-4 border-b border-border pb-3">
        <h3 className="col-span-12 font-mono text-[11px] tracking-[0.16em] uppercase text-foreground">
          {group.label}
        </h3>
      </div>
      <ul className="divide-y divide-border">
        {group.rows.map((row) => (
          <ComparisonRow key={row.feature} row={row} colKeys={colKeys} />
        ))}
      </ul>
    </section>
  );
}

function ComparisonRow({row, colKeys}) {
  return (
    <li className="grid grid-cols-12 items-center gap-4 py-3.5 md:py-4">
      <div className="col-span-6 text-[14px] leading-snug text-foreground">
        {row.feature}
      </div>
      {colKeys.map((k, i) => (
        <div key={k || i} className="col-span-3">
          <ComparisonCell value={row[k]} primary={i === 0} />
        </div>
      ))}
    </li>
  );
}

function ComparisonCell({value, primary}) {
  if (value === true) {
    return (
      <CheckIcon
        className={`size-4 ${primary ? 'text-primary' : 'text-foreground-subtle'}`}
      />
    );
  }
  if (value === false || value === null || value === undefined) {
    return <span className="text-foreground-subtle">, </span>;
  }
  return (
    <span
      className={`text-[13.5px] ${primary ? 'text-foreground' : 'text-foreground-muted'}`}
    >
      {value}
    </span>
  );
}

/* ---------------------------------------------------------------------------
 * 9 - Plans (3-tier pricing + add-ons, opt-in via content.plans)
 *
 * Renders inline plan cards on the product page itself, so visitors can
 * compare plans without leaving the page. Section element gets id="plans"
 * so other CTAs (e.g. the comparison column header) can scroll to it
 * with an in-page anchor.
 * ------------------------------------------------------------------------ */

function SectionPlans({content, anchorId, total}) {
  const {plans} = content;
  const t = useTranslations('Templates.ProductPage.anchors');
  if (!plans || !plans.tiers || plans.tiers.length === 0) return null;

  return (
    <section id="plans" className="section-rhythm scroll-mt-24">
      <Container>
        <SectionAnchor id={anchorId} label={t('pricing')} total={total} />

        <div className="grid grid-cols-1 gap-10 md:grid-cols-12 md:gap-12">
          <div className="md:col-span-7">
            <Reveal>
              <h2 className="headline-section text-foreground max-w-[22ch]">
                {plans.headline}
              </h2>
            </Reveal>
          </div>
          <div className="md:col-span-5 md:flex md:flex-col md:justify-end">
            <Reveal delay={0.06}>
              <p className="lead max-w-[44ch]">{plans.lead}</p>
            </Reveal>
          </div>
        </div>

        <RevealStagger
          mode="clone"
          className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-3"
          gap={0.08}
        >
          {plans.tiers.map((tier) => (
            <PlanCard key={tier.name} tier={tier} />
          ))}
        </RevealStagger>

        {plans.addOns && plans.addOns.items && plans.addOns.items.length > 0 && (
          <div className="mt-20">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-12 md:gap-12">
              <div className="md:col-span-7">
                <Reveal>
                  <h3 className="text-2xl font-medium text-foreground tracking-tight md:text-[28px]">
                    {plans.addOns.headline}
                  </h3>
                </Reveal>
              </div>
              {plans.addOns.lead && (
                <div className="md:col-span-5 md:flex md:flex-col md:justify-end">
                  <Reveal delay={0.06}>
                    <p className="text-[15px] leading-relaxed text-foreground-muted max-w-[42ch]">
                      {plans.addOns.lead}
                    </p>
                  </Reveal>
                </div>
              )}
            </div>

            <RevealStagger
              mode="clone"
              className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-3"
              gap={0.08}
            >
              {plans.addOns.items.map((item) => (
                <AddOnCard key={item.name} item={item} />
              ))}
            </RevealStagger>
          </div>
        )}

        {plans.footnote && (
          <Reveal>
            <p className="mt-12 text-center text-[13px] text-foreground-subtle">
              {plans.footnote}
            </p>
          </Reveal>
        )}
      </Container>
    </section>
  );
}

function PlanCard({tier}) {
  const featured = !!tier.featured;
  return (
    <article
      className={`surface-elevated relative flex h-full flex-col p-7 ${
        featured ? 'ring-1 ring-primary/40' : ''
      }`}
    >
      {tier.eyebrow && (
        <span className="absolute right-5 top-5 inline-flex items-center rounded-full border border-brand-200 bg-brand-50 px-2.5 py-1 font-mono text-[10px] tracking-[0.12em] uppercase text-brand-700">
          {tier.eyebrow}
        </span>
      )}

      <div>
        <p className="fig-label">{tier.name}</p>
        {tier.tagline && (
          <p className="mt-2 text-[15px] leading-relaxed text-foreground-muted max-w-[36ch]">
            {tier.tagline}
          </p>
        )}
        <div className="mt-6 flex items-baseline gap-2">
          <span className="text-4xl font-medium text-foreground tracking-tight">
            {tier.price}
          </span>
          {tier.period && (
            <span className="text-[14px] text-foreground-muted">
              {tier.period}
            </span>
          )}
        </div>
        {tier.priceNote && (
          <p className="mt-1 text-[13px] text-foreground-subtle">
            {tier.priceNote}
          </p>
        )}
      </div>

      {tier.cta && (
        <div className="mt-7">
          <MaybeLink
            href={tier.cta.href}
            className={`${featured ? 'cta-pill-brand' : 'cta-pill'} w-full justify-center`}
          >
            {tier.cta.label} <span aria-hidden>&rarr;</span>
          </MaybeLink>
        </div>
      )}

      {tier.features && tier.features.length > 0 && (
        <div className="mt-8 border-t border-border pt-6">
          {tier.featuresLabel && (
            <p className="fig-label mb-4">{tier.featuresLabel}</p>
          )}
          <ul className="space-y-3">
            {tier.features.map((feature) => (
              <li
                key={feature}
                className="flex items-start gap-3 text-[14px] leading-relaxed text-foreground"
              >
                <CheckIcon className="mt-1 size-3.5 flex-shrink-0 text-primary" />
                <span>{feature}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </article>
  );
}

function AddOnCard({item}) {
  return (
    <article className="surface-elevated flex h-full flex-col p-6">
      <div className="flex items-baseline justify-between gap-3">
        <p className="fig-label">{item.name}</p>
        <p className="font-mono text-[11px] tracking-[0.08em] uppercase text-foreground-subtle whitespace-nowrap">
          {item.unit}
        </p>
      </div>
      <p className="mt-4 text-3xl font-medium text-foreground tracking-tight">
        {item.price}
      </p>
      {item.body && (
        <p className="mt-3 flex-1 text-[14px] leading-relaxed text-foreground-muted">
          {item.body}
        </p>
      )}
    </article>
  );
}

/* ---------------------------------------------------------------------------
 * 10 - Testimonial + pricing teaser
 * ------------------------------------------------------------------------ */

function PricingRateCard({rate, rateUnit}) {
  if (!rate) return null;

  return (
    <div className="surface-elevated p-8 md:p-10">
      <div className="flex items-baseline gap-2">
        <span className="text-4xl font-medium text-foreground tracking-tight md:text-[42px]">
          {rate}
        </span>
      </div>
      {rateUnit && (
        <p className="mt-2 text-[14px] text-foreground-muted">{rateUnit}</p>
      )}
    </div>
  );
}

function SectionTestimonialAndPricing({content, anchorId, total}) {
  const {testimonial, pricing} = content;
  const tA = useTranslations('Templates.ProductPage.anchors');
  const tT = useTranslations('Templates.ProductPage.testimonial');
  const tP = useTranslations('Templates.ProductPage.pricing');
  if (!testimonial && !pricing) return null;

  const both = !!testimonial && !!pricing;
  const label = both ? tA('proofAndPricing') : pricing ? tA('pricing') : tA('customer');
  const testimonialColClass = both
    ? 'md:col-span-7'
    : 'md:col-span-10 md:col-start-2';
  const pricingOnlyWithRate = pricing && !testimonial && pricing.rate;
  // Pricing teaser with no testimonial and no rate card: nothing fills the
  // right column, so centre and widen the copy instead of leaving it stranded
  // in a 5-of-12 block with empty space beside it.
  const pricingOnlyNoRate = pricing && !testimonial && !pricing.rate;
  const pricingCopyColClass = pricingOnlyWithRate ? 'md:col-span-7' : 'md:col-span-5';

  const renderPricingCopy = (centered) =>
    pricing && (
      <>
        <Reveal delay={0.06}>
          <p className="fig-label">{tP('pricingLabel')}</p>
        </Reveal>
        <Reveal delay={0.12}>
          <h3
            className={`headline-manifesto mt-4 max-w-[16ch] text-foreground${
              centered ? ' md:mx-auto md:max-w-[24ch]' : ''
            }`}
          >
            {pricing.headline}
          </h3>
        </Reveal>
        <Reveal delay={0.18}>
          <p className={`lead mt-6 max-w-[36ch]${centered ? ' md:mx-auto md:max-w-[54ch]' : ''}`}>
            {pricing.body}
          </p>
        </Reveal>
        <Reveal delay={0.24}>
          <MaybeLink href={pricing.ctaHref} className="cta-text-brand mt-8">
            {pricing.ctaLabel} <span aria-hidden>&rarr;</span>
          </MaybeLink>
        </Reveal>
      </>
    );

  return (
    <section className="section-rhythm">
      <Container>
        <SectionAnchor id={anchorId} label={label} total={total} />

        <div className="grid grid-cols-1 gap-10 md:grid-cols-12 md:gap-12">
          {testimonial && (
            <div className={testimonialColClass}>
              <Reveal>
                <figure className="surface-elevated p-8 md:p-10">
                  <p className="font-mono text-[11px] tracking-[0.16em] uppercase text-foreground-subtle">
                    {tT('customerLabel')}
                  </p>
                  <blockquote className="mt-5 text-2xl font-medium leading-snug text-foreground tracking-tight md:text-[28px]">
                    &ldquo;{testimonial.quote}&rdquo;
                  </blockquote>
                  <figcaption className="mt-7 flex items-center gap-3 border-t border-border pt-5 text-[13px]">
                    <span className="font-medium text-foreground">
                      {testimonial.author}
                    </span>
                    <span className="text-foreground-subtle">&middot;</span>
                    <span className="text-foreground-muted">
                      {testimonial.role}, {testimonial.company}
                    </span>
                  </figcaption>
                </figure>
              </Reveal>
            </div>
          )}

          {pricing && pricingOnlyWithRate && (
            <>
              <div className={`${pricingCopyColClass} md:flex md:flex-col md:justify-center`}>
                {renderPricingCopy(false)}
              </div>
              <div className="md:col-span-5 md:flex md:flex-col md:justify-center">
                <Reveal delay={0.12}>
                  <PricingRateCard rate={pricing.rate} rateUnit={pricing.rateUnit} />
                </Reveal>
              </div>
            </>
          )}

          {pricing && both && (
            <div className={`${pricingCopyColClass} md:flex md:flex-col md:justify-center`}>
              {pricing.rate && (
                <Reveal delay={0.06}>
                  <PricingRateCard rate={pricing.rate} rateUnit={pricing.rateUnit} />
                </Reveal>
              )}
              {renderPricingCopy(false)}
            </div>
          )}

          {pricing && pricingOnlyNoRate && (
            <div className="md:col-span-8 md:col-start-3 md:flex md:flex-col md:items-center md:justify-center md:text-center">
              {renderPricingCopy(true)}
            </div>
          )}
        </div>
      </Container>
    </section>
  );
}

/* ---------------------------------------------------------------------------
 * 9 - Closing CTA
 * ------------------------------------------------------------------------ */

function SectionClosing({content, anchorId, total}) {
  const {closing} = content;
  const t = useTranslations('Templates.ProductPage.anchors');
  return (
    <section className="section-rhythm-lg">
      <Container>
        <SectionAnchor id={anchorId} label={t('getStarted')} total={total} />
        <div className="grid grid-cols-1 gap-12 md:grid-cols-12 md:gap-12">
          <div className="md:col-span-7">
            <Reveal as="h2" className="headline-manifesto text-foreground max-w-[22ch]">
              {closing.headline}
            </Reveal>
            <Reveal delay={0.06}>
              <p className="lead mt-6 max-w-[44ch]">{closing.body}</p>
            </Reveal>
            <Reveal delay={0.12}>
              <div className="mt-10 flex flex-wrap items-center gap-5">
                <Link href={closing.primaryCta.href} className="cta-pill-brand">
                  {closing.primaryCta.label} <span aria-hidden>&rarr;</span>
                </Link>
                <Link href={closing.secondaryCta.href} className="cta-text-brand">
                  {closing.secondaryCta.label} <span aria-hidden>&rarr;</span>
                </Link>
              </div>
            </Reveal>
          </div>
          {closing.benefits && closing.benefits.length > 0 && (
            <div className="md:col-span-5">
              <RevealStagger
                as="ul"
                mode="clone"
                className="space-y-3 text-[13px] text-foreground-muted md:border-l md:border-border md:pl-12"
                gap={0.05}
              >
                {closing.benefits.map((b) => (
                  <li key={b} className="flex items-start gap-3">
                    <CheckIcon className="mt-1 size-3.5 flex-shrink-0 text-primary" />
                    <span>{b}</span>
                  </li>
                ))}
              </RevealStagger>
            </div>
          )}
        </div>
      </Container>
    </section>
  );
}

/* ---------------------------------------------------------------------------
 * Page composition
 *
 * Total = hero + capabilities + (n deep dives) + use cases + integrations
 *       + testimonial/pricing + closing
 *
 * Sections collapse if their content is missing (use cases, integrations,
 * testimonial/pricing) but anchor numbering reflects only present sections.
 * ------------------------------------------------------------------------ */

export default function ProductPageV2({content}) {
  const t = useTranslations('Templates.ProductPage.anchors');
  const deepDives = content.deepDives || [];
  const sections = [
    'hero',
    'capabilities',
    ...deepDives.map((_, i) => `deep-${i}`),
    content.useCases ? 'useCases' : null,
    content.integrations ? 'integrations' : null,
    content.comparison ? 'comparison' : null,
    content.plans ? 'plans' : null,
    content.testimonial || content.pricing ? 'testimonial' : null,
    'closing'
  ].filter(Boolean);

  const total = sections.length;
  const anchorOf = (key) => sections.indexOf(key) + 1;

  /* "Deep dive {n}" — pad as 01/02/03 for the gutter label.
   * Renders translated for non-EN locales while keeping the same numeric
   * cadence the design relies on. */
  const deepDiveLabel = (i) => t('deepDive', {n: String(i + 1).padStart(2, '0')});

  return (
    <>
      <SectionHero content={content} total={total} />
      <SectionCapabilities content={content} total={total} />
      {deepDives.map((entry, i) => (
        <React.Fragment key={`deep-${i}`}>
          <SectionDivider />
          <SectionDeepDive
            entry={entry}
            index={i}
            anchorId={anchorOf(`deep-${i}`)}
            label={deepDiveLabel(i)}
            total={total}
          />
        </React.Fragment>
      ))}
      {content.useCases && (
        <>
          <SectionDivider />
          <SectionUseCases content={content} anchorId={anchorOf('useCases')} total={total} />
        </>
      )}
      {content.integrations && (
        <SectionIntegrations content={content} anchorId={anchorOf('integrations')} total={total} />
      )}
      {content.comparison && (
        <>
          <SectionDivider />
          <SectionComparison
            content={content}
            anchorId={anchorOf('comparison')}
            total={total}
          />
        </>
      )}
      {content.plans && (
        <>
          <SectionDivider />
          <SectionPlans
            content={content}
            anchorId={anchorOf('plans')}
            total={total}
          />
        </>
      )}
      {(content.testimonial || content.pricing) && (
        <>
          <SectionDivider />
          <SectionTestimonialAndPricing
            content={content}
            anchorId={anchorOf('testimonial')}
            total={total}
          />
        </>
      )}
      <SectionDivider />
      <SectionClosing content={content} anchorId={anchorOf('closing')} total={total} />
    </>
  );
}
